import type { ConnectError } from '@connectrpc/connect'
import {
  createConnectQueryKey,
  useMutation as useConnectMutation,
} from '@connectrpc/connect-query'
import {
  issueProcurementPlanXmlUploadUrl,
  listProcurementPlans,
  uploadProcurementPlanXml,
} from '@ebalance/proto/bffuser/v1/procurement_plan_service-ProcurementPlanService_connectquery'
import { useMutation, useQueryClient } from '@tanstack/react-query'

/** 発行 response の上限 (max_content_bytes) を超えるファイルを選んだ。PUT も取込もしない。 */
export class UploadFileTooLargeError extends Error {
  readonly maxContentBytes: bigint
  constructor(maxContentBytes: bigint) {
    super('upload file too large')
    this.name = 'UploadFileTooLargeError'
    this.maxContentBytes = maxContentBytes
  }
}

/** 発行 URL への PUT が失敗した (network error / !res.ok)。取込 RPC は呼ばない。 */
export class UploadPutFailedError extends Error {
  constructor() {
    super('upload PUT failed')
    this.name = 'UploadPutFailedError'
  }
}

export type UploadProcurementPlanXmlError =
  | ConnectError
  | UploadFileTooLargeError
  | UploadPutFailedError
  | Error

/**
 * 需要調達計画 XML / ZIP の取込 hook (signed URL upload 設計 Decision #19)。
 *
 * 手順: 発行 RPC → `file.size > maxContentBytes` なら中止 → 発行 URL へ PUT
 * (response の method / headers をそのまま使う) → 取込 RPC (`uploadToken`)。
 * isPending は 3 段を通して 1 つ。取込 RPC 成功で一覧を invalidate する。
 *
 * organization_id は BFF が eauth から補完するため frontend には存在しない。
 */
export function useUploadProcurementPlanXml(): {
  mutate: (
    file: File,
    options: {
      onSuccess?: () => void
      onError?: (err: UploadProcurementPlanXmlError) => void
    },
  ) => void
  isPending: boolean
} {
  const queryClient = useQueryClient()
  const { mutateAsync: issueUploadUrl } = useConnectMutation(
    issueProcurementPlanXmlUploadUrl,
  )
  const { mutateAsync: importXml } = useConnectMutation(
    uploadProcurementPlanXml,
  )

  const { mutate, isPending } = useMutation<
    void,
    UploadProcurementPlanXmlError,
    File
  >({
    mutationFn: async (file) => {
      const issued = await issueUploadUrl({})
      if (BigInt(file.size) > issued.maxContentBytes) {
        throw new UploadFileTooLargeError(issued.maxContentBytes)
      }
      const upload = issued.upload
      // backend / BFF は upload を必ず返す契約。未設定は契約違反で、
      // 利用者の再試行で直る PUT 失敗とは区別する。
      if (upload === undefined) {
        throw new Error('issued upload request is missing')
      }
      const res = await fetch(upload.url, {
        method: upload.method,
        headers: upload.headers,
        body: file,
      }).catch(() => {
        throw new UploadPutFailedError()
      })
      if (!res.ok) throw new UploadPutFailedError()
      await importXml({ uploadToken: issued.uploadToken })
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listProcurementPlans,
          cardinality: 'finite',
        }),
      })
    },
  })

  return {
    mutate: (file, options) => {
      mutate(file, options)
    },
    isPending,
  }
}
